import React from 'react';
import { Link } from 'react-router-dom';
import { mockAboutData } from '../mock/mockData';
import { Award, Heart, Users } from 'lucide-react';

const AboutMe = () => {
  return (
    <div style={{ backgroundColor: 'var(--color-white)' }}>
      {/* Hero Section */}
      <section className="section-spacing-large" style={{ backgroundColor: 'var(--color-gray-100)' }}>
        <div className="container-artworld">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--spacing-2xl)',
            alignItems: 'center'
          }}>
            <div>
              <div className="type-indicator" style={{ marginBottom: 'var(--spacing-sm)' }}>
                About the Artist
              </div>
              <h1 className="section-title" style={{ marginBottom: 'var(--spacing-md)' }}>
                {mockAboutData.name}
              </h1>
              <p className="artist-name" style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--color-gray-600)' }}>
                {mockAboutData.title}
              </p>
              <p className="caption-text" style={{ marginBottom: 'var(--spacing-md)', fontStyle: 'italic' }}>
                (She/Her) • Calgary, Canada
              </p>
              
              <div className="body-text" style={{ marginBottom: 'var(--spacing-md)' }}>
                I am a multidisciplinary artist, interior designer, and art educator living and working in Calgary (Moh-kins-tsis). My practice is rooted in community connection, cultural expression, and creative exploration. I am passionate about creating opportunities for neighbours and diverse communities to connect through art, and I believe that art can support healing, mental well-being, and meaningful relationships.
              </div>
              
              <div className="body-text" style={{ marginBottom: 'var(--spacing-md)' }}>
                During the pandemic, I began volunteering with non-profit organizations and facilitating art workshops to help people feel less isolated and more connected. These experiences allowed me to explore my own creativity while sharing ideas and techniques with others. Since then, community engagement and teaching have become an important part of my artistic practice.
              </div>
              
              <div className="body-text" style={{ marginBottom: 'var(--spacing-md)' }}>
                My artwork has been exhibited in community venues and art spaces across Calgary and Canmore. Through my work, I aim to celebrate heritage, foster a sense of belonging, and create inclusive spaces where people of all ages and backgrounds can connect and express themselves.
              </div>
              
              <div className="body-text" style={{ marginBottom: 'var(--spacing-md)' }}>
                My work explores culture, memory, and the natural world. I create with mixed media and recycled materials, bringing together tradition and personal experience in a way that feels natural and intuitive. My pieces tell stories and express emotions, inviting viewers to pause, reflect, and connect. Through my art, I aim to create meaningful connections and encourage thoughtful conversations.
              </div>
              
              <div className="body-text">
                My ongoing series uses shredded paper to create paper-mâché sculptures and functional pieces that combine art with everyday use. Through rebuilding and reshaping paper, I explore themes of mental health, healing, and resilience. By using recycled materials, my work also encourages conversations about care for the environment, sustainability, consumption, and the life of materials beyond their original purpose.
              </div>
            </div>
            
            <div style={{
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
            }}>
              <img
                src="https://customer-assets.emergentagent.com/job_creative-studio-292/artifacts/ebs3xpap_saloni.jpg"
                alt={mockAboutData.name}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing-large">
        <div className="container-artworld">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="section-title" style={{ marginBottom: 'var(--spacing-md)' }}>
              Artistic Statement
            </h2>
            <p className="body-text" style={{ fontSize: '1.125rem', lineHeight: '1.8', fontStyle: 'italic' }}>
              "{mockAboutData.statement}"
            </p>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--color-gray-100)' }}>
        <div className="container-artworld">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            Expertise & Experience
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--spacing-xl)'
          }}>
            {/* Specializations */}
            <div style={{
              backgroundColor: 'var(--color-white)',
              padding: 'var(--spacing-xl)',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{ color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>
                <Heart size={40} />
              </div>
              <h3 className="artist-name" style={{ marginBottom: 'var(--spacing-md)', fontSize: '1.5rem' }}>
                Specializations
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Water Colour Paint',
                  'Acrylic Painting',
                  'Clay Moulding & 3D Art',
                  'Props Making',
                  'Flower Making & Weaving',
                  'Upcycling Art Projects',
                  'Community Art Workshops'
                ].map((spec, index) => (
                  <li key={index} className="body-text" style={{ marginBottom: 'var(--spacing-sm)' }}>
                    • {spec}
                  </li>
                ))}
              </ul>
            </div>

            {/* Collaborative Projects */}
            <div style={{
              backgroundColor: 'var(--color-white)',
              padding: 'var(--spacing-xl)',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{ color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>
                <Users size={40} />
              </div>
              <h3 className="artist-name" style={{ marginBottom: 'var(--spacing-md)', fontSize: '1.5rem' }}>
                Collaborative Art Projects
              </h3>
              <p className="caption-text" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-gray-600)' }}>
                Community Groups
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Acrylic Painting for Seniors',
                  'Glow in the paint night',
                  'Weaving Workshops',
                  'Card Making Sessions',
                  'Lantern Making',
                  'Folk Art Workshops (Warli, Gond, Madhubani)',
                  'Clay Moulding & 3D Art Workshops',
                  'Props Making Workshops',
                  'Bracelets Making',
                  'Bead animals',
                  'Tie And Dye',
                  'Flower Making',
                  'String Art',
                  'Fridge Magnets'
                ].map((project, index) => (
                  <li key={index} className="body-text" style={{ marginBottom: 'var(--spacing-sm)', fontSize: '0.95rem' }}>
                    • {project}
                  </li>
                ))}
              </ul>
            </div>

            {/* Mindful Art */}
            <div style={{
              backgroundColor: 'var(--color-white)',
              padding: 'var(--spacing-xl)',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{ color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>
                <Award size={40} />
              </div>
              <h3 className="artist-name" style={{ marginBottom: 'var(--spacing-md)', fontSize: '1.5rem' }}>
                Mindful Art Workshops
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Mandala Creation',
                  'Zentangles',
                  'Art Journaling',
                  'Neurological Art'
                ].map((workshop, index) => (
                  <li key={index} className="body-text" style={{ marginBottom: 'var(--spacing-sm)' }}>
                    • {workshop}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing-large">
        <div className="container-artworld">
          <div style={{
            backgroundColor: 'var(--color-gray-900)',
            color: 'var(--color-white)',
            padding: 'var(--spacing-2xl)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <h2 className="section-title" style={{ color: 'var(--color-white)', marginBottom: 'var(--spacing-md)' }}>
              Explore My Work & Join an Event
            </h2>
            <p className="body-text" style={{ 
              color: 'var(--color-gray-300)', 
              maxWidth: '600px', 
              margin: '0 auto var(--spacing-xl)',
              fontSize: '1.125rem'
            }}>
              Discover my artwork in the gallery or join me at an upcoming workshop or event
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/gallery" style={{ textDecoration: 'none' }}>
                <button className="btn-primary" style={{
                  backgroundColor: 'var(--color-white)',
                  color: 'var(--color-primary)',
                  borderColor: 'var(--color-white)'
                }}>
                  View Gallery
                </button>
              </Link>
              <Link to="/events" style={{ textDecoration: 'none' }}>
                <button className="btn-inverse" style={{
                  color: 'var(--color-white)',
                  borderColor: 'var(--color-white)'
                }}>
                  Upcoming Events
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutMe;
